import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../config/api';
import Layout from './Layout';
import AddonModal from './AddonModal';
import ConfirmModal from './ConfirmModal';
import { showError, showSuccess } from '../utils/toast';
import './Addons.css';

const Addons = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAddons, setTotalAddons] = useState(0);
  const lastFetchedPage = useRef(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    confirmButtonType: 'danger'
  });

  useEffect(() => {
    // Reset the ref if page changed (allow fetch for new page)
    if (lastFetchedPage.current !== null && lastFetchedPage.current !== currentPage) {
      lastFetchedPage.current = null;
    }

    // Prevent double call in React StrictMode for the same page
    if (lastFetchedPage.current === currentPage) {
      return;
    }

    // Mark this page as fetched
    lastFetchedPage.current = currentPage;

    fetchAddons();
  }, [currentPage]);

  const fetchAddons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }

      // Fetch addons from API
      const response = await apiClient.get('/admin/addons', token);

      console.log('Addons API Response:', response);

      let addonsArray = [];
      let totalPagesCount = 1;
      let totalAddonsCount = 0;

      // Handle API response structure
      if (response && response.data) {
        if (response.data.addons && Array.isArray(response.data.addons)) {
          addonsArray = response.data.addons;
          totalAddonsCount = addonsArray.length;
          totalPagesCount = Math.ceil(totalAddonsCount / pageSize) || 1;
        } else if (Array.isArray(response.data)) {
          addonsArray = response.data;
          totalAddonsCount = response.total || addonsArray.length;
          totalPagesCount = Math.ceil(totalAddonsCount / pageSize) || 1;
        }
      } else if (Array.isArray(response)) {
        addonsArray = response;
        totalAddonsCount = response.length;
        totalPagesCount = Math.ceil(totalAddonsCount / pageSize) || 1;
      } else if (response && response.addons && Array.isArray(response.addons)) {
        addonsArray = response.addons;
        totalAddonsCount = response.total || addonsArray.length;
        totalPagesCount = Math.ceil(totalAddonsCount / pageSize) || 1;
      }

      console.log('Extracted addons array:', addonsArray);
      console.log('Addons count:', addonsArray.length);
      console.log('Total addons:', totalAddonsCount);
      console.log('Total pages:', totalPagesCount);

      // Apply pagination
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedAddons = addonsArray.slice(startIndex, endIndex);

      setAddons(paginatedAddons);
      setTotalPages(totalPagesCount);
      setTotalAddons(totalAddonsCount);
    } catch (error) {
      console.error('Error fetching addons:', error);
      showError('Failed to fetch addons: ' + (error.message || 'Unknown error'));
      setAddons([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) return 'Free';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const handleAddAddon = () => {
    setEditingAddon(null);
    setShowAddonModal(true);
  };

  const handleEditAddon = (addon) => {
    console.log('handleEditAddon - Addon object being passed to modal:', addon);
    setEditingAddon(addon);
    setShowAddonModal(true);
  };

  const handleAddonUpdated = (updatedAddon) => {
    console.log('handleAddonUpdated called with:', updatedAddon);
    // Update the addon in local state immediately with the updated data
    setAddons(prevAddons => {
      const updated = prevAddons.map(a => {
        if (a.id === updatedAddon.id) {
          return {
            ...a,
            ...updatedAddon
          };
        }
        return a;
      });
      return updated;
    });
  };

  const handleStatusToggle = (addon) => {
    const action = addon.is_active ? 'deactivate' : 'activate';

    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Addon`,
      message: `Are you sure you want to ${action} this addon?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancel',
      confirmButtonType: addon.is_active ? 'danger' : 'primary',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          // Toggle the status
          const updatedAddon = { ...addon, is_active: !addon.is_active };

          // Use the specific endpoint for updating addon details
          await apiClient.put(`/admin/addons/${addon.id}`, updatedAddon, token);

          // Update local state
          setAddons(prevAddons => prevAddons.map(a =>
            a.id === addon.id ? { ...a, is_active: updatedAddon.is_active } : a
          ));

          showSuccess(`Addon ${action}d successfully`);
        } catch (error) {
          console.error('Error updating addon status:', error);
          showError('Failed to update addon status: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleDeleteAddon = (addon) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Addon',
      message: `Are you sure you want to delete "${addon.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonType: 'danger',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          await apiClient.delete(`/admin/addons/${addon.id}`, token);

          // Remove from local state
          setAddons(prevAddons => prevAddons.filter(a => a.id !== addon.id));
          setTotalAddons(prev => prev - 1);

          showSuccess('Addon deleted successfully');

          // Refresh the list if current page becomes empty
          if (addons.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            fetchAddons();
          }
        } catch (error) {
          console.error('Error deleting addon:', error);
          showError('Failed to delete addon: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleAddonModalClose = () => {
    setShowAddonModal(false);
    setEditingAddon(null);
    // Refresh addons after add/edit with a small delay to ensure API update is complete
    setTimeout(() => {
      lastFetchedPage.current = null;
      fetchAddons();
    }, 300);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const halfMax = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    // Adjust start/end if near boundaries
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    if (endPage - startPage + 1 < maxPagesToShow) {
      endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key={1} className="pagination-number" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-number"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return (
      <Layout>
        <div className="addons-loading">
          <div className="loading-spinner">Loading addons...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="addons-page">
        {/* Header Section */}
        <div className="addons-page-header">
          <div className="header-left">
            <h1 className="page-title">Addons</h1>
          </div>
          <div className="filter-tabs-right">
            {/* Filters and Menu buttons hidden per request */}
            <button className="add-addon-btn" onClick={handleAddAddon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Addon
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="addons-table-wrapper">
          <table className="addons-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Addon Name</th>
                <th>Tokens</th>
                <th>Price</th>
                <th>Status</th>
                <th>Date Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {addons.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-addons">
                    <div className="empty-state">
                      {loading ? 'Loading...' : 'No addons found'}
                    </div>
                  </td>
                </tr>
              ) : (
                addons.map((addon, index) => {
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;
                  const isActive = addon.is_active;

                  return (
                    <tr key={addon.id}>
                      <td className="row-number">{rowNumber}</td>
                      <td>
                        <div className="addon-name-cell">
                          <div className="addon-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                              <path d="M2 17l10 5 10-5"></path>
                              <path d="M2 12l10 5 10-5"></path>
                            </svg>
                          </div>
                          <div className="addon-name-info">
                            <div className="addon-name">{addon.name || 'N/A'}</div>
                            {addon.description && (
                              <div className="addon-details">
                                {addon.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="addon-tokens-value">{addon.tokens || 0}</span>
                      </td>
                      <td>
                        <span className="addon-price-value">{formatPrice(addon.price_usd)}</span>
                      </td>
                      <td>
                        <div
                          className={`status-indicator ${isActive ? 'positive' : 'negative'}`}
                          onClick={() => handleStatusToggle(addon)}
                          style={{ cursor: 'pointer' }}
                          title={`Click to ${isActive ? 'deactivate' : 'activate'} addon`}
                        >
                          <span className="status-arrow">
                            {isActive ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            )}
                          </span>
                          <span className="status-text">{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                        </div>
                      </td>
                      <td className="date-cell">{formatDate(addon.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEditAddon(addon)}
                            title="Edit Addon"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteAddon(addon)}
                            title="Delete Addon"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showAddonModal && (
          <AddonModal
            addon={editingAddon}
            onClose={handleAddonModalClose}
            onAddonUpdated={handleAddonUpdated}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          confirmButtonType={confirmModal.confirmButtonType}
        />

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="table-pagination">
            <div className="pagination-left">
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => {
                  console.log('Page size changed to:', e.target.value);
                }}
              >
                <option value="10">10 / page</option>
                <option value="15">15 / page</option>
                <option value="20">20 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
              </select>
            </div>

            <div className="pagination-right">
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div className="pagination-numbers">
                {renderPaginationNumbers()}
              </div>

              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Addons;

